import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import React, { memo, useContext, useMemo, useState, useEffect } from 'react';
import { LayoutContext } from '../../contexts/LayoutContext';
import { PageContext } from '../../contexts/PageContext';
import { Blok } from '../../interfaces/Blok.interface';
import TestingGrid from '../TestingGrid/TestingGrid.molecule';
import {
  InstantSearch,
  InstantSearchSSRProvider,
  Configure,
} from 'react-instantsearch-hooks-web';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import algoliasearch from 'algoliasearch/lite';
import ArticleIndexPageHero from './ArticleIndexPageHero';
import { GroupedArticleIndexPageResultsBlokProps } from '../GroupedArticleIndexPageResults/GroupedArticleIndexPageResults';
import { FlatArticleIndexPageResultsBlokProps } from '../FlatArticleIndexPageResults/FlatArticleIndexPageResults';
import { getQueryParam } from '../../utils/getQueryParam/getQueryParam.util';
import BreadcrumbBar from '../BreadcrumbBar/BreadcrumbBar';
import ArticleIndexPageLoading from './ArticleIndexPageLoading';
import Cookies from 'js-cookie';

interface ArticleIndexPageBlokProps extends Blok {
  hero_background_image: StoryblokAsset;
  hero_heading: string;
  hero_body?: string;
  hero_search_bar_placeholder: string;
  searchable_category_tags?: string[];
  results?:
    | GroupedArticleIndexPageResultsBlokProps[]
    | FlatArticleIndexPageResultsBlokProps[];
  body?: Blok[];
  show_contact_bar: boolean;
  lead_source_reference?: string;
}

interface ArticleIndexPageProps {
  blok: ArticleIndexPageBlokProps;
}

const ArticleIndexPage: React.FC<ArticleIndexPageProps> = ({ blok }) => {
  const { header, footer, contact_bar, breadcrumbs } =
    useContext(LayoutContext);
  const {
    hero_background_image,
    hero_heading,
    hero_body = undefined,
    hero_search_bar_placeholder,
    searchable_category_tags = [],
    results = [],
    body = [],
    show_contact_bar,
  } = blok;

  if (!Cookies.get('lead_source_reference') && blok?.lead_source_reference) {
    Cookies.set('lead_source_reference', blok.lead_source_reference, {
      expires: 7,
    });
  }

  const indexName: string = useMemo(() => {
    return `${process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME}`;
  }, [process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME]);

  const algoliaSearchClient = useMemo(() => {
    return algoliasearch(
      `${process.env.GATSBY_ALGOLIA_APP_ID}`,
      `${process.env.GATSBY_ALGOLIA_SEARCH_API_KEY}`
    );
  }, [
    process.env.GATSBY_ALGOLIA_APP_ID,
    process.env.GATSBY_ALGOLIA_SEARCH_API_KEY,
  ]);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const filters = useMemo(() => {
    const baseFilters: string = 'field_component:article';
    if (!searchable_category_tags.length) return baseFilters;
    return `${baseFilters} AND ${
      searchable_category_tags.length > 1 ? '(' : ''
    }${searchable_category_tags
      .map(
        (searchableCategoryTag: string) =>
          `category_tags:"${searchableCategoryTag}"`
      )
      .join(' OR ')}${searchable_category_tags.length > 1 ? ')' : ''}`;
  }, [searchable_category_tags]);

  const hitsPerPage = useMemo(() => {
    if (!results.length) return;
    return results[0].number_of_results_per_page;
  }, [results]);

  /*-- 
    initially the plan was useState and useEffect, 
    but this didn't work with the InstantSearch 
    component for some reason
  --*/
  const getInitialQuery = (): string => {
    if (typeof window === 'undefined') return '';
    return getQueryParam('q') || '';
  };

  const getInitialPage = (): number => {
    if (typeof window === 'undefined') return 0;
    return parseInt(getQueryParam('page')!) || 0;
  };

  /*-- useEffects --*/
  useEffect(() => {
    /*--
      The react instantsearch plugin is taking a moment before 
      returning and rendering, so we are getting hit on cls 
      issues. We will hack a loading state to give it time to 
      render properly on the page. This will improve lighthouse 
      score
    --*/
    setTimeout(() => {
      setIsLoading(false);
    }, 400);
  }, []);

  return (
    <PageContext.Provider
      value={{
        headerIsTransparent: false,
        isHeaderSearchFunctionalityRequired: true,
        showContactBar: show_contact_bar,
      }}
    >
      <div className="flex flex-col min-h-screen justify-between">
        {header && (
          <StoryblokComponent blok={header.content} key={header._uid} />
        )}
        <main
          className="mb-auto min-h-screen relative"
          {...storyblokEditable(blok)}
        >
          {/*-- for lighthouse --*/}
          {/* {isLoading && (
            <ArticleIndexPageLoading
              loadingType={
                results
                  ?.map((result) => result.component)
                  .includes('grouped_article_index_page_results')
                  ? 'grouped'
                  : 'flat'
              }
            />
          )} */}
          <InstantSearchSSRProvider>
            <InstantSearch
              indexName={indexName}
              searchClient={algoliaSearchClient}
              initialUiState={{
                [indexName]: {
                  query: getInitialQuery(),
                  page: getInitialPage(),
                },
              }}
            >
              <Configure filters={filters} hitsPerPage={hitsPerPage} />
              <ArticleIndexPageHero
                hero_background_image={hero_background_image}
                hero_heading={hero_heading}
                hero_body={hero_body}
                hero_search_bar_placeholder={hero_search_bar_placeholder}
              />
              {breadcrumbs && <BreadcrumbBar breadcrumbs={breadcrumbs} />}
              {results?.map(
                (
                  result:
                    | GroupedArticleIndexPageResultsBlokProps
                    | FlatArticleIndexPageResultsBlokProps
                ) => (
                  <StoryblokComponent
                    blok={{ ...result, isLoading: isLoading }}
                    key={result._uid}
                  />
                )
              )}
            </InstantSearch>
          </InstantSearchSSRProvider>
          {!isLoading && (
            <>
              {body?.map((nestedBlok: Blok) => (
                <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
              ))}
              {show_contact_bar && contact_bar && (
                <StoryblokComponent
                  blok={contact_bar.content}
                  key={contact_bar._uid}
                />
              )}
            </>
          )}
        </main>
        {footer && (
          <StoryblokComponent blok={footer.content} key={footer._uid} />
        )}
      </div>
      <TestingGrid />
    </PageContext.Provider>
  );
};

export default memo(ArticleIndexPage);
